import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const GOLD = "#D4AF37";
const DURATION_MS = 1600; // shorter, non-blocking

export default function SplashScreen({ onFinish }) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(false), DURATION_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <AnimatePresence onExitComplete={onFinish}>
      {visible && (
        <motion.div
          data-testid="splash-screen"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
          style={{
            background:
              "radial-gradient(120% 120% at 50% 40%, #141210 0%, #0a0a0a 55%, #050505 100%)",
            pointerEvents: "auto",
          }}
        >
          {/* Ambient gold glow */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 0.6, scale: 1 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="pointer-events-none absolute"
            style={{
              width: 460,
              height: 460,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0.06) 45%, transparent 70%)",
              filter: "blur(12px)",
            }}
          />

          <div className="relative z-10 flex flex-col items-center px-6 text-center">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <motion.div
                aria-hidden
                className="absolute inset-0 rounded-full"
                animate={{ opacity: [0.4, 0.7, 0.4] }}
                transition={{
                  duration: 1.6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{
                  boxShadow: `0 0 45px 8px rgba(212,175,55,0.45)`,
                }}
              />
              <div
                className="relative flex items-center justify-center overflow-hidden rounded-full"
                style={{
                  height: 96,
                  width: 96,
                  border: `2px solid ${GOLD}`,
                  background: "#0a0a0a",
                  boxShadow: "0 10px 40px rgba(0,0,0,0.6)",
                }}
              >
                <img
                  src="/autorio-logo.jpg"
                  alt="AutoRIO"
                  className="h-full w-full object-cover"
                  draggable={false}
                />
              </div>
            </motion.div>

            {/* Brand name */}
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mt-6 text-3xl font-extrabold tracking-tight text-white sm:text-4xl"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Auto<span style={{ color: GOLD }}>RIO</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-white/50"
            >
              Dealer Premium · Suceava
            </motion.p>
          </div>

          {/* Bottom loader */}
          <div className="absolute bottom-14 left-0 right-0 flex flex-col items-center gap-3 px-8">
            <div
              className="h-[2px] w-full max-w-[200px] overflow-hidden rounded-full"
              style={{ background: "rgba(255,255,255,0.08)" }}
            >
              <motion.div
                className="h-full rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: DURATION_MS / 1000, ease: "linear" }}
                style={{
                  background: `linear-gradient(90deg, rgba(212,175,55,0.4), ${GOLD})`,
                  boxShadow: `0 0 10px ${GOLD}`,
                }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
